import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '#/api/client';
import { mutationNotif } from '#/utils/mutationNotif';

import {
  MESSAGE_TEMPLATE_ENDPOINT_BASE,
  MESSAGE_QUERY_KEY_NAMESPACE,
} from './utils.ts';
import { MessageTemplateSchema } from '../model/index.ts';
import {
  FileBag,
  MessageUserInput,
  toMessageUserInput,
  ValidMessageFormValue,
} from '../model/messageForm.ts';

export const toMultipartFormData = (
  payload: MessageUserInput,
  files: FileBag,
): FormData => {
  const fd = new FormData();

  for (const [key, value] of Object.entries(payload)) {
    if (value === null || value === undefined) continue;
    if (key === 'thumbUrl') continue;
    fd.append(
      key,
      typeof value === 'object' ? JSON.stringify(value) : String(value),
    );
  }

  if (files.thumbFile) fd.append('thumbFile', files.thumbFile);
  if (files.userListFile) fd.append('userListFile', files.userListFile);
  for (const [key, file] of Object.entries(files.actionImages)) {
    fd.append(`actionImages[${key}]`, file);
  }

  return fd;
};

export async function createMessageTemplate(input: {
  payload: MessageUserInput;
  files: FileBag;
}) {
  const fd = toMultipartFormData(input.payload, input.files);
  const response = await apiClient.post(MESSAGE_TEMPLATE_ENDPOINT_BASE, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const parsed = MessageTemplateSchema.safeParse(response.data.data);
  if (!parsed.success) {
    console.error(parsed.error);
    throw new Error('Invalid create response from server');
  }
  return parsed.data;
}

export function useCreateMessageTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formValue: ValidMessageFormValue) => {
      const userInput = toMessageUserInput(formValue);

      return createMessageTemplate(userInput);
    },
    onMutate: () => {
      const NOTIF_ID = 'create-message-template';
      mutationNotif.loading(NOTIF_ID, {
        title: '建立訊息',
        message: '建立中...',
      });
      return { notifId: NOTIF_ID };
    },
    onSuccess: (_, __, context) => {
      queryClient.invalidateQueries({
        queryKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplates'],
      });
      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '已建立');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(context.notifId, '失敗請稍後再試');
    },
  });
}


export async function updateMessageTemplate(input: {
  payload: MessageUserInput;
  files: FileBag;
}) {
  const fd = toMultipartFormData(input.payload, input.files);
  const response = await apiClient.put(MESSAGE_TEMPLATE_ENDPOINT_BASE, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const parsed = MessageTemplateSchema.safeParse(response.data.data);
  if (!parsed.success) {
    console.error(parsed.error);
    throw new Error('Invalid create response from server');
  }
  return parsed.data;
}


export function useUpdateMessageTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (formValue: ValidMessageFormValue) => {
      const userInput = toMessageUserInput(formValue);

      return createMessageTemplate(userInput);
    },
    onMutate: () => {
      const NOTIF_ID = 'create-message-template';
      mutationNotif.loading(NOTIF_ID, {
        title: '建立訊息',
        message: '更新中...',
      });
      return { notifId: NOTIF_ID };
    },
    onSuccess: (_, __, context) => {
      queryClient.invalidateQueries({
        queryKey: [MESSAGE_QUERY_KEY_NAMESPACE, 'messageTemplates'],
      });
      if (!context?.notifId) return;
      mutationNotif.toSuccess(context.notifId, '已更新');
    },
    onError: (_, __, context) => {
      if (!context?.notifId) return;
      mutationNotif.toError(context.notifId, '失敗請稍後再試');
    },
  });
}